import { Link } from 'react-router-dom';
import { Bed, Bath, Maximize, MapPin } from 'lucide-react';
import { useCurrency } from '@/contexts/CurrencyContext';
import { formatPrice as fmtPrice } from '@/lib/formatPrice';
import { optimizedImageUrl } from '@/lib/imageOptimizer';

interface MapPropertyCardProps {
  property: any;
  active?: boolean;
  onHover?: (id: string | null) => void;
}

export function MapPropertyCard({ property, active, onHover }: MapPropertyCardProps) {
  const { displayCurrency, rate } = useCurrency();
  const images = property.property_images || [];
  const imgRaw = images.find((i: any) => i.is_primary)?.url || images[0]?.url
    || 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=400&h=300&fit=crop';
  const img = optimizedImageUrl(imgRaw, { width: 240, height: 180, quality: 70 });

  const title = typeof property.title === 'object'
    ? (property.title.en || Object.values(property.title)[0] || '')
    : (property.title || '');

  const slug = property.slug || property.id;
  const priceLabel = fmtPrice(property.price, property.currency || 'ETB', displayCurrency, rate);


  return (
    <Link
      to={`/property/${slug}`}
      onMouseEnter={() => onHover?.(property.id)}
      onMouseLeave={() => onHover?.(null)}
      className={`flex gap-3 p-2 rounded-lg border transition-all ${
        active ? 'border-primary shadow-md bg-primary/5' : 'border-border bg-card hover:border-primary/40'
      }`}
    >
      <img
        src={img}
        alt={title}
        loading="lazy"
        className="w-28 h-24 rounded-md object-cover shrink-0"
      />
      <div className="flex-1 min-w-0 flex flex-col justify-between py-0.5">
        <div>
          <p className="text-sm font-bold text-primary truncate">{priceLabel}</p>
          <h3 className="text-xs font-semibold line-clamp-1 mt-0.5">{title}</h3>
          <p className="text-[10px] text-muted-foreground flex items-center gap-1 mt-0.5">
            <MapPin className="h-2.5 w-2.5" />
            <span className="truncate">{property.city || property.location || ''}</span>
          </p>
        </div>
        <div className="flex items-center gap-2.5 text-[10px] text-muted-foreground mt-1">
          {property.bedrooms != null && (
            <span className="flex items-center gap-0.5"><Bed className="h-3 w-3" />{property.bedrooms}</span>
          )}
          {property.bathrooms != null && (
            <span className="flex items-center gap-0.5"><Bath className="h-3 w-3" />{property.bathrooms}</span>
          )}
          {property.area_sqm && (
            <span className="flex items-center gap-0.5"><Maximize className="h-3 w-3" />{property.area_sqm}m²</span>
          )}
        </div>
      </div>
    </Link>
  );
}
